// Library Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Local Imports
import endPoints from '@/redux/constant';
import axios from '@/redux/http';
import { normalizeRole, ROLES } from '@/router/roles';
import { PURGE } from 'redux-persist';
import { updateLocationAPIFn } from '../MediaKit/mediakitSlice';

// Initial State
const initialState = {
  loading: false,
  error: null,
  currentUser: null,
  mediaKit: null,
  mediaKitLive: null,
  mediaKitLoading: false,
  isReviewDoItLater: false,
  currentTheme: 'standard',
  previewThemeColors: null,
  previewThemeFonts: null
};

// Login User API Fn
export const loginUserAPIFn = createAsyncThunk(
  'auth/currentUser',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(endPoints.login, {
        ...values
      });
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// Login User API Fn
export const loginUserWpAPIFn = createAsyncThunk(
  'auth/currentUserWp',
  async (values, { rejectWithValue }) => {
    try {
      const response = await fetch(endPoints.loginWp, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'sso-system-token': 'ctIEBiAbOfO[qmD$2@WlK1w|ED1+AEpCh*71Re=k@w-&XNVg5',
        },
        credentials: 'include',
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err?.message);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// Login User API Fn
export const logoutUserWpAPIFn = createAsyncThunk(
  'auth/logoutUserWp',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetch(endPoints.logoutWp, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "sso-system-token": "ctIEBiAbOfO[qmD$2@WlK1w|ED1+AEpCh*71Re=k@w-&XNVg5",
          "Cache-Control": "no-cache",
        },
      });

      await fetch(endPoints.logoutWp, {
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      });

      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// Register User API Fn
export const registerUserAPIFn = createAsyncThunk(
  'auth/registerUser',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(endPoints.register, {
        ...values,
        isWpUser_ysn: false,
      });

      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.response?.data);
    }
  }
);

// Get User Plan Type
export const getUserPlanTypeAPIFn = createAsyncThunk(
  'auth/usePlanType',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${endPoints.userPlanTypeURL}?userId=${values?.userId}`);
      return data?.result;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

// GET MEDIA KIT INFO
export const getMediaKitInfoAPIFn = createAsyncThunk(
  'mediakit/getMediaKitInfoAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${endPoints.getMediaKitDetails}?userId=${values?.userId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

// GET MEDIA KIT INFO
export const getLiveMediaKitInfoAPIFn = createAsyncThunk(
  'mediakit/getLiveMediaKitInfoAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${endPoints.getLiveMediaKitDetails}?liveMediaKitLink=${values?.link}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

// SET USER PASSWORD
export const setUserPasswordAPIFn = createAsyncThunk(
  'mediakit/setUserPasswordAPIFn',
  async (values, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${endPoints.setPassword}`, {
        ...values
      });
      return data;
    } catch (error) {
      return rejectWithValue(error?.response);
    }
  }
);

// Login User Slices
export const currentUserSlice = createSlice({
  name: 'auth/authentication',
  initialState,
  reducers: {
    logout: () => initialState,
    updateCurrentUser: (state, action) => {
      state.currentUser = {
        ...state.currentUser,
        ...action.payload
      };
    },
    updateInstagramAuthStatus: (state, action) => {
      state.mediaKit.isInstagramAuthentcated = action.payload;
    },
    updateNewUser: (state, action) => {
      state.currentUser.isNewUser = action.payload;
    },
    updateUserPlan: (state, action) => {
      state.currentUser.planType = action.payload;
    },
    updateMediakitReviewForLater: (state, action) => {
      state.isReviewDoItLater = action.payload;
    },
    updateMediaKitType: (state, action) => {
      state.mediaKit.mediaKit.isSampleKit = action.payload;
    },
    updateTheme: (state, action) => {
      state.currentTheme = action.payload;
    },
    setPreviewThemeColors: (state, action) => {
      state.previewThemeColors = action.payload;
    },
    clearPreviewThemeColors: state => {
      state.previewThemeColors = null;
    },
    setPreviewThemeFonts: (state, action) => {
      state.previewThemeFonts = action.payload;
    },
    clearPreviewThemeFonts: state => {
      state.previewThemeFonts = null;
    },
    updateLiveMediaKit: (state, action) => {
      state.mediaKitLive = action.payload;
    },

    upsertTopPostLocally: (state, action) => {
      if (!state.mediaKit) return;
      const { id, mediaKitId, socialChannelName, postIndex, freePost, paidPost } = action.payload || {};
      const topPosts = Array.isArray(state.mediaKit.topPosts) ? [...state.mediaKit.topPosts] : [];

      const matchIndex =
        id != null
          ? topPosts.findIndex(item => item?.id === id)
          : topPosts.findIndex(item => item?.postIndex === postIndex);

      const existingRow = matchIndex > -1 ? topPosts[matchIndex] : null;
      const updatedRow = {
        ...existingRow,
        id: existingRow?.id ?? id ?? null,
        mediaKitId: mediaKitId ?? existingRow?.mediaKitId,
        socialChannelName: socialChannelName ?? existingRow?.socialChannelName,
        postIndex: postIndex ?? existingRow?.postIndex,
        freePost,
        paidPost
      };

      if (matchIndex > -1) {
        topPosts[matchIndex] = updatedRow;
      } else {
        topPosts.push(updatedRow);
      }

      state.mediaKit.topPosts = topPosts;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUserAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
        state.mediaKitLoading = false;
      })
      .addCase(loginUserAPIFn.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = {
          accessToken: action.payload?.accessToken || null,
          email: action.payload?.email || null,
          emailConfirmed: action.payload?.emailConfirmed || null,
          isInstagramAuthentcated: action.payload?.isInstagramAuthentcated || null,
          lastLogin: action.payload?.lastLoginDateTime_dtm || null,
          message: action.payload?.message || null,
          refreshToken: action.payload?.refreshToken || null,
          registerationDateTime: action.payload?.registerationDateTime_dtm || null,
          status: action.payload?.status || null,
          termAndConditionAcceptDateTime:
            action.payload?.termAndConditionAcceptDateTime_dtm || null,
          termAndConditionLink: action.payload?.termAndConditionLink_chr || null,
          userId: action.payload?.userId || null,
          // Every account this app creates is a creator — if the backend doesn't return
          // a recognized role, default to creator instead of leaving the guard stranded.
          userRole: normalizeRole(action.payload?.userRole) || ROLES.CREATOR,
          username: action.payload?.username || null,
          isNewUser: false,
          isEmailVerified: false,
        };
        state.error = null;
        state.mediaKit = null;
        state.mediaKitLoading = false;
      })
      .addCase(loginUserAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.mediaKitLoading = false;
      })
      .addCase(loginUserWpAPIFn.fulfilled, (state, action) => {
        const wpData = action.payload?.data || {};
        state.loading = false;
        state.currentUser = {
          accessToken: action.payload?.accessToken || action.payload?.token || null,
          email: wpData.user_email || action.payload?.email || action.payload?.user_email || null,
          emailConfirmed: action.payload?.emailConfirmed ?? true,
          isInstagramAuthentcated: action.payload?.isInstagramAuthentcated || null,
          lastLogin: action.payload?.lastLoginDateTime_dtm || null,
          message: action.payload?.message || null,
          refreshToken: action.payload?.refreshToken || null,
          registerationDateTime: action.payload?.registerationDateTime_dtm || null,
          status: action.payload?.status || null,
          termAndConditionAcceptDateTime: action.payload?.termAndConditionAcceptDateTime_dtm || null,
          termAndConditionLink: action.payload?.termAndConditionLink_chr || null,
          userId: wpData.user_id || action.payload?.userId || action.payload?.user_id || null,
          userRole: normalizeRole(wpData.type || action.payload?.userRole) || ROLES.CREATOR,
          username: action.payload?.username || action.payload?.user_nicename || null,
          isNewUser: false,
          isEmailVerified: false,
        };
        state.error = null;
        state.mediaKit = null;
        state.mediaKitLoading = false;
      })
      .addCase(loginUserWpAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.mediaKitLoading = false;
      })
      .addCase(registerUserAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
        if (!state.currentUser?.accessToken) {
          state.currentUser = null;
          state.mediaKit = null;
        }
        state.mediaKitLoading = false;
      })
      .addCase(registerUserAPIFn.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.mediaKit = null;
        state.mediaKitLoading = false;
      })
      .addCase(registerUserAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!state.currentUser?.accessToken) {
          state.currentUser = null;
          state.mediaKit = null;
        }
        state.mediaKitLoading = false;
      })
      // USER PLAN TYPE DATA
      .addCase(getUserPlanTypeAPIFn.pending, state => {
        state.loading = true;
        state.error = null;
        state.mediaKitLoading = false;
      })
      .addCase(getUserPlanTypeAPIFn.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = {
          ...state.currentUser,
          planType:
            action.payload === 'User is paid'
              ? 'paid'
              : action.payload === 'User has cancelled their subscription'
                ? 'cancelled'
                : 'free'
        };
        state.error = null;
        state.mediaKitLoading = false;
      })
      .addCase(getUserPlanTypeAPIFn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.mediaKitLoading = false;
      })
      .addCase(getMediaKitInfoAPIFn.pending, state => {
        state.mediaKitLoading = true;
        state.error = null;
        state.loading = false;
      })
      .addCase(getMediaKitInfoAPIFn.fulfilled, (state, action) => {
        state.mediaKitLoading = false;
        state.mediaKit = action.payload?.result;
        state.currentUser.isNewUser = action.payload?.result?.mediaKit?.isSavedPassword
          ? false
          : state.currentUser.isNewUser || false;
        state.error = null;
        state.loading = false;
        state.isReviewDoItLater = false;
      })
      .addCase(getMediaKitInfoAPIFn.rejected, (state, action) => {
        state.mediaKitLoading = false;
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(setUserPasswordAPIFn.pending, state => {
        state.currentUser.isNewUser = false;
      })
      .addCase(setUserPasswordAPIFn.fulfilled, (state) => {
        state.currentUser.isNewUser = true;
      })
      .addCase(setUserPasswordAPIFn.rejected, (state) => {
        state.currentUser.isNewUser = false;
      })
      .addCase(updateLocationAPIFn.pending, (state, action) => {
        const payload = action.meta?.arg;
        if (state.mediaKit?.mediaKit && payload) {
          state.mediaKit.mediaKit.country = payload.country_chr || null;
          state.mediaKit.mediaKit.city = payload.city_chr || null;
          state.mediaKit.mediaKit.isCountryIncluded = Boolean(payload.isCountryIncluded);
        }
      })
      .addCase(updateLocationAPIFn.fulfilled, (state, action) => {
        const payload = action.meta?.arg;
        if (state.mediaKit?.mediaKit && payload) {
          state.mediaKit.mediaKit.country = payload.country_chr || null;
          state.mediaKit.mediaKit.city = payload.city_chr || null;
          state.mediaKit.mediaKit.isCountryIncluded = Boolean(payload.isCountryIncluded);
        }
      })
      .addCase(PURGE, () => initialState);
  }
});

// Action creators are generated for each case reducer function
export const {
  logout,
  updateNewUser,
  updateMediaKitType,
  updateUserPlan,
  updateMediakitReviewForLater,
  updateTheme,
  setPreviewThemeColors,
  clearPreviewThemeColors,
  setPreviewThemeFonts,
  clearPreviewThemeFonts,
  updateLiveMediaKit,
  updateCurrentUser,
  updateInstagramAuthStatus,
  upsertTopPostLocally
} = currentUserSlice.actions;

export default currentUserSlice.reducer;
